import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Callout } from "@promptgatewaysadmin/ui";
import { usePortalClient } from "../api/PortalClientProvider.js";
import { useSession } from "../auth/SessionProvider.js";
import { invalidateAfterHandoff, openPortalLink } from "../queries/hooks.js";
import { useOnline } from "./states.js";
import { errorCopy } from "../copy/errors.js";
import { toPortalError } from "./states.js";
import { useHandoffReturn } from "../hooks/useHandoffReturn.js";

/**
 * The `portal-link` handoff — Stripe in a NEW TAB.
 *
 * Used by Update card, Edit address, Manage seats, Change cycle, Downgrade, Cancel, Resume.
 * A side errand: the customer keeps the portal open, so there is no back-button ambiguity
 * and no lost state. Checkout is the other pattern and is a full redirect.
 *
 * Every handoff button carries a trailing ↗ and an aria-label suffix "(opens Stripe in a
 * new tab)" — a consistent, learnable signal that the next click leaves the portal.
 *
 * The URL is ONE-TIME: created on click, never cached, never rendered into an href at
 * render time. A portal-link sitting in the DOM is a live billing session waiting to be
 * shared.
 */
export function HandoffButton({
  label,
  variant = "secondary",
  size = "md",
  /** Shown before navigating; the cancel flow uses it for reassurance-first copy. */
  confirm,
  disabledReason,
}: {
  label: string;
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md" | "lg";
  confirm?: { title: string; body: string; proceed: string } | undefined;
  disabledReason?: string;
}) {
  const client = usePortalClient();
  const qc = useQueryClient();
  const { orgId } = useSession();
  const online = useOnline();
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [confirming, setConfirming] = useState(false);

  // The refetch on return is the whole design: every mutation happens on Stripe's site, so
  // this is the only way the portal learns about it. Miss it and the customer returns to a
  // screen still showing their old plan — which reads as the change having failed.
  useHandoffReturn(() => invalidateAfterHandoff(qc, orgId!));

  const go = async () => {
    setBusy(true);
    setFailure(null);
    setBlocked(false);
    try {
      const outcome = await openPortalLink(client);
      if (outcome === "blocked") setBlocked(true);
    } catch (e) {
      const mapped = toPortalError(e);
      // A 409 here is mapped to NotAnError and never reaches this branch — there is simply
      // no Stripe customer yet, which is the normal case for every trialing org.
      if (mapped) setFailure(e);
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  const unavailable = disabledReason ?? (!online ? "You're offline." : undefined);

  return (
    <div className="pg-handoff">
      {confirming && confirm ? (
        <Callout tone="info" title={confirm.title}>
          {confirm.body}{" "}
          <Button size="sm" variant="secondary" onClick={() => setConfirming(false)}>
            Keep my plan
          </Button>{" "}
          <Button size="sm" variant="danger" onClick={() => void go()} disabled={busy}>
            {confirm.proceed} ↗
          </Button>
        </Callout>
      ) : (
        <Button
          variant={variant}
          size={size}
          disabled={busy}
          aria-disabled={unavailable ? true : undefined}
          aria-describedby={unavailable ? "handoff-reason" : undefined}
          aria-label={`${label} (opens Stripe in a new tab)`}
          onClick={() => {
            if (unavailable) return;
            if (confirm) setConfirming(true);
            else void go();
          }}
        >
          {busy ? "Opening Stripe…" : `${label} ↗`}
        </Button>
      )}

      {unavailable && (
        <span id="handoff-reason" className="pg-disabled-reason">
          {unavailable}
        </span>
      )}

      {blocked && (
        <Callout tone="warn" role="alert">
          {errorCopy("error.billing.popupBlocked").body}{" "}
          <Button size="sm" variant="ghost" onClick={() => void go()}>
            Open Stripe billing ↗
          </Button>
        </Callout>
      )}

      {failure != null && (
        <Callout tone="danger" role="alert">
          {errorCopy(toPortalError(failure)?.copyKey ?? "error.billing.unavailable").body}
        </Callout>
      )}
    </div>
  );
}
